import React, { useEffect, useMemo, useRef, useState } from "react"

import { OneVerifyEraUploadFileFormat } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element.tsx"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"

import {
    EraUploadFileUploadStatus,
    INTEGRATIONS_CONFIG,
} from "../../../config/integrations.config"
import { EraUploadHelper } from "../../../helpers/eraUpload.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { integrationsUploadEraFiles } from "../../../store/thunks/integrations.thunks"
import type {
    EraUploadReviewPopupMode,
    IEraUploadBatchReviewState,
    IEraUploadCompletionState,
    IEraUploadFileReviewState,
    IEraUploadUploadProgress,
} from "../../../types/integrations.types"
import { EraUploadBulkUploadPopupComponent } from "../../popups/eraUploadBulkUploadPopup.component"
import { EraUploadReviewFileSectionComponent } from "./eraUploadReviewFileSection.component"
import { EraUploadReviewFloatingControlComponent } from "./eraUploadReviewFloatingControl.component"
import { EraUploadUploadProgressOverlayComponent } from "./eraUploadUploadProgressOverlay.component"

import closeDarkImg from "@front-shared/assets/images/icons/close-dark.svg"
import plusIcon from "@front-shared/assets/images/icons/plus-dark.svg"

export const EraUploadReviewComponent: React.FunctionComponent<
    IEraUploadReviewComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const runtimeState = useAppSelector(runtimeSelector)

    const isUploading = useMemo<boolean>(() => {
        return RuntimeHelper.isIntegrationsEraUploadLoading()
    }, [runtimeState.isLoading])

    const [uploadProgress, setUploadProgress] =
        useState<IEraUploadUploadProgress | null>(null)

    const [popupMode, setPopupMode] = useState<EraUploadReviewPopupMode | null>(
        null,
    )

    const [replaceFileId, setReplaceFileId] = useState<string | null>(null)

    const [selectedFileId, setSelectedFileId] = useState<string | null>(
        () => props.state.files[0]?.id ?? null,
    )

    const batchRef = useRef(props.state)

    batchRef.current = props.state

    useEffect(() => {
        if (
            selectedFileId &&
            props.state.files.some(file => file.id === selectedFileId)
        ) {
            return
        }

        setSelectedFileId(props.state.files[0]?.id ?? null)
    }, [props.state.files, selectedFileId])

    const selectedFile = useMemo<IEraUploadFileReviewState | undefined>(() => {
        return props.state.files.find(file => file.id === selectedFileId)
    }, [props.state.files, selectedFileId])

    const fileSelectOptions = useMemo<ISelectRenderedOption[]>(() => {
        return props.state.files.map(file => {
            const hasValidationIssue =
                !file.isValid ||
                file.uploadStatus === EraUploadFileUploadStatus.failed

            return {
                value: file.id,
                displayValue: file.file.name,
                dropdownDisplayValue: (
                    <div className="flex items-center gap-[8px] min-w-0">
                        <span
                            aria-hidden
                            className={`
                                shrink-0 w-[6px] h-[6px] rounded-full
                                ${
                                    hasValidationIssue
                                        ? "bg-brand-error"
                                        : "bg-brand-green3"
                                }
                            `}
                        />

                        <PrimaryText className="truncate">
                            {file.file.name}
                        </PrimaryText>
                    </div>
                ),
            }
        })
    }, [props.state.files])

    const batchSummary = useMemo(() => {
        return EraUploadHelper.getBatchSummary(props.state)
    }, [props.state])

    const isX12Upload =
        props.state.fileFormat === OneVerifyEraUploadFileFormat.x12

    const remainingFileSlots =
        INTEGRATIONS_CONFIG.eraUpload.maxFilesPerUpload -
        props.state.files.length

    const canAddFiles = remainingFileSlots > 0

    const subtitle = useMemo(() => {
        if (isX12Upload) {
            if (batchSummary.invalidFileCount > 0) {
                return `${batchSummary.validFileCount} of ${batchSummary.totalFileCount} X12 files ready for upload.`
            }

            return `${batchSummary.validFileCount} X12 file${batchSummary.validFileCount === 1 ? "" : "s"} ready for upload.`
        }

        if (batchSummary.isCsvPreviewTruncated) {
            return `Previewing rows per file — ${batchSummary.totalCsvRowCount.toLocaleString()} total rows across ${batchSummary.validFileCount} file${batchSummary.validFileCount === 1 ? "" : "s"}.`
        }

        if (batchSummary.invalidFileCount > 0) {
            return `${batchSummary.validFileCount} of ${batchSummary.totalFileCount} files ready.`
        }

        return null
    }, [batchSummary, isX12Upload])

    const updateBatch = (batch: IEraUploadBatchReviewState) => {
        props.onBatchChange(batch)
    }

    const uploadFiles = (fileIds: string[]) => {
        if (!selectedPracticeRole || !fileIds.length) {
            return
        }

        const currentBatch = batchRef.current

        const filesToUpload = EraUploadHelper.getUploadableFiles(
            currentBatch,
        ).filter(file => fileIds.includes(file.id))

        if (!filesToUpload.length) {
            return
        }

        setUploadProgress({
            uploadedCount: 0,
            totalCount: filesToUpload.length,
        })

        dispatch(
            integrationsUploadEraFiles({
                practice: selectedPracticeRole.practice,
                payload: {
                    batchId: currentBatch.batchId,
                    batchTimestamp: currentBatch.batchTimestamp,
                    fileFormat: currentBatch.fileFormat,
                    files: filesToUpload.map(file => {
                        return {
                            fileId: file.id,
                            file: file.file,
                            fileFormat: currentBatch.fileFormat,
                            rowCount: file.totalRowCount,
                        }
                    }),
                    onUploadProgress: progress => {
                        setUploadProgress(progress)
                    },
                    onFileStatusChange: (fileId, update) => {
                        const nextBatch = EraUploadHelper.updateFileInBatch(
                            batchRef.current,
                            fileId,
                            update,
                        )

                        batchRef.current = nextBatch

                        updateBatch(nextBatch)
                    },
                },
                onSuccess: () => {
                    if (
                        EraUploadHelper.areAllValidFilesUploaded(
                            batchRef.current,
                        )
                    ) {
                        props.onUploadComplete(
                            EraUploadHelper.buildCompletionState(
                                batchRef.current,
                            ),
                        )
                    }
                },
                onFinally: () => {
                    setUploadProgress(null)
                },
            }),
        )
    }

    const onUpload = () => {
        const fileIds = EraUploadHelper.getUploadableFiles(props.state).map(
            file => file.id,
        )

        uploadFiles(fileIds)
    }

    const onRetryUpload = (fileId: string) => {
        uploadFiles([fileId])
    }

    const onRemoveFile = (fileId: string) => {
        const nextBatch = EraUploadHelper.removeFileFromBatch(
            props.state,
            fileId,
        )

        if (!nextBatch.files.length) {
            props.onClose()

            return
        }

        updateBatch(nextBatch)
    }

    const onReplaceFile = (fileId: string) => {
        setReplaceFileId(fileId)

        setPopupMode("replace")
    }

    const onAddFiles = () => {
        setReplaceFileId(null)

        setPopupMode("add")
    }

    const onPopupFilesValidated = (files: IEraUploadFileReviewState[]) => {
        if (!files.length) {
            return
        }

        if (popupMode === "replace" && replaceFileId) {
            updateBatch(
                EraUploadHelper.replaceFileInBatch(
                    props.state,
                    replaceFileId,
                    files[0],
                ),
            )
        } else if (popupMode === "add") {
            updateBatch(
                EraUploadHelper.appendFilesToBatch(props.state, files),
            )
        }

        setPopupMode(null)

        setReplaceFileId(null)
    }

    const closePopup = () => {
        setPopupMode(null)

        setReplaceFileId(null)
    }

    return (
        <div className={`pb-32 relative w-full ${props.className ?? ""}`}>
            <EraUploadUploadProgressOverlayComponent
                isActive={isUploading}
                progress={uploadProgress}
            />

            <div className="max-w-[960px] w-full">
                <div className="relative py-[12px] mb-[24px]">
                    <div className="flex items-center flex-wrap gap-[16px]">
                        <PrimaryText
                            size={29}
                            lineHeight={35}
                            weight="semibold"
                            className="mr-[8px]"
                        >
                            Review ERA Upload
                        </PrimaryText>

                        <span
                            className="
                                flex items-center px-[8px] py-[2px]
                                rounded-[4px] bg-brand-white
                            "
                        >
                            <PrimaryText typography="h6" className="mr-[4px]">
                                {batchSummary.totalFileCount} file
                                {batchSummary.totalFileCount === 1 ? "" : "s"}
                            </PrimaryText>

                            <button
                                type="button"
                                onClick={props.onClose}
                                className="p-[4px] cursor-pointer"
                                aria-label="Close review"
                            >
                                <img src={closeDarkImg} alt="Close" />
                            </button>
                        </span>

                        {canAddFiles ? (
                            <ButtonElement
                                label="Add files"
                                htmlType="button"
                                type="default"
                                icon={plusIcon}
                                onClick={onAddFiles}
                                disabled={isUploading}
                                buttonClassName="!pl-[12px] !pr-[12px]"
                            />
                        ) : null}

                        {subtitle ? (
                            <PrimaryText
                                typography="subtitle"
                                className="w-full sm:w-auto"
                                color={NIRVANA_COLORS.brand.grey7}
                            >
                                {subtitle}
                            </PrimaryText>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="max-w-[960px] w-full pb-[120px]">
                <div
                    className="
                        w-full mb-[16px] rounded-[4px] bg-brand-white
                        px-[8px] py-[4px]
                    "
                >
                    <SelectElement
                        className="w-full"
                        name="eraUploadReviewFile"
                        placeholder="Select a file"
                        externalValue={selectedFile?.file.name}
                        renderedOptions={fileSelectOptions}
                        onSelected={setSelectedFileId}
                        getPopupContainer={() => document.body}
                        inlined
                    />
                </div>

                {selectedFile ? (
                    <EraUploadReviewFileSectionComponent
                        fileFormat={props.state.fileFormat}
                        file={selectedFile}
                        isBatchUploading={isUploading}
                        onRemoveFile={onRemoveFile}
                        onReplaceFile={onReplaceFile}
                        onRetryUpload={onRetryUpload}
                    />
                ) : null}
            </div>

            <EraUploadReviewFloatingControlComponent
                summary={batchSummary}
                canAddFiles={canAddFiles}
                onAddFiles={onAddFiles}
                onUpload={onUpload}
                isUploading={isUploading}
            />

            <EraUploadBulkUploadPopupComponent
                isActive={popupMode !== null}
                mode={popupMode ?? "add"}
                fileFormat={props.state.fileFormat}
                maxFiles={
                    popupMode === "replace"
                        ? 1
                        : remainingFileSlots
                }
                singleFileOnly={popupMode === "replace"}
                onDone={closePopup}
                onFilesValidated={onPopupFilesValidated}
            />
        </div>
    )
}

interface IEraUploadReviewComponentProps {
    className?: string
    state: IEraUploadBatchReviewState
    onBatchChange: (state: IEraUploadBatchReviewState) => void
    onClose: () => void
    onUploadComplete: (state: IEraUploadCompletionState) => void
}
